import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Tag } from '~/components';
import { Form, type FormProps } from '~/components/Form';
import { FormFieldInput, FormFieldTextArea } from '~/components/FormField';
import { Label } from '~/components/Label';
import { cn } from '~/lib/cn';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { ExpandableRows } from '~settings/components/Scrollable/ExpandableRows';
import { SelectLabels } from '~settings/components/SelectLabels';
import { SETTINGS_ROUTES } from '~settings/config/routes';

import { type LabelGroupFormSchema, labelGroupFormSchema } from './schema';

type LabelGroupFormProps = Omit<
  FormProps<LabelGroupFormSchema>,
  'formHook' | 'schema'
> & {
  readOnly?: boolean;
};
export function LabelGroupForm({
  values,
  onSubmit,
  className,
  readOnly = false,
  ...formProps
}: LabelGroupFormProps) {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const form = useForm<LabelGroupFormSchema>({
    defaultValues: {
      name: '',
      labels: [],
      notes: '',
    },
    values,
    resolver: zodResolver(labelGroupFormSchema, {
      errorMap: getI18nErrorMap((s) => t(`validations.${s}`)),
    }),
    mode: 'onBlur',
  });

  return (
    <Form
      formHook={form}
      onSubmit={readOnly ? () => {} : onSubmit}
      aria-label={t('labelGroup.formDescription')}
      schema={labelGroupFormSchema}
      {...formProps}
    >
      <div className="flex flex-col gap-md rounded-sm p-lg bg-level-02">
        <FormFieldInput
          name="name"
          label={t('labelGroup.fields.name')}
          placeholder={t('name')}
          className="w-full max-w-[26rem]"
          readOnly={readOnly}
        />
        {!readOnly ? (
          <SelectLabels name="labels" className="w-full max-w-[52rem]" />
        ) : (
          <div
            className={cn(
              'flex w-full max-w-[52rem] flex-col gap-sm',
              className,
            )}
          >
            <Label id="labels" name="labels">
              {t('labelGroup.fields.labels')}
            </Label>
            {values?.labels?.length ? (
              <ExpandableRows maxVisibleRows={10} visibleRows={5}>
                {values?.labels?.map((label) => (
                  <Tag
                    key={label.value}
                    variant="secondary"
                    size="lg"
                    asChild
                    onClick={() =>
                      navigate(SETTINGS_ROUTES.LABEL_DETAIL(label.value))
                    }
                    className="max-w-full cursor-pointer break-all"
                  >
                    <li>{label.label}</li>
                  </Tag>
                ))}
              </ExpandableRows>
            ) : null}
          </div>
        )}

        <div
          className={cn('flex w-full max-w-[52rem] flex-col gap-sm', className)}
        >
          <Label id="users" name="users">
            {t('labelGroup.fields.users')}
          </Label>
          <p className="text-body-sm text-subtle">
            {t('labelGroup.usersNotice')}
          </p>
          {values?.users?.length ? (
            <ExpandableRows maxVisibleRows={10} visibleRows={5}>
              {values?.users?.map((user) => (
                <Tag
                  key={user.value}
                  variant="secondary"
                  size="lg"
                  asChild
                  onClick={() =>
                    navigate(SETTINGS_ROUTES.USER_DETAIL(user.value))
                  }
                  className="cursor-pointer"
                >
                  <li>{user.label}</li>
                </Tag>
              ))}
            </ExpandableRows>
          ) : null}
        </div>
      </div>
      <div className="rounded-sm p-lg bg-level-02">
        <FormFieldTextArea
          name="notes"
          label={t('labelGroup.fields.notes')}
          placeholder={t('placeholders.notes')}
          className="w-full max-w-[52rem]"
          readOnly={readOnly}
        />
      </div>
    </Form>
  );
}

LabelGroupForm.displayName = 'LabelGroup';
